import React from "react";
import DatePicker from "react-datepicker";
import {
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  isAfter,
  isSameDay,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from "date-fns";
import { CalendarDays, ChevronLeft, ChevronRight, Search } from "lucide-react";
import "./CSS/ReportPeriodPicker.css";

export type CalendarReportPeriod = "week" | "biweek" | "month" | "quarter" | "year";
export type ReportPeriod = CalendarReportPeriod | "custom" | "all";

interface PeriodRange {
  start: Date;
  end: Date;
}

const PERIODS: { value: CalendarReportPeriod; label: string }[] = [
  { value: "week", label: "Weekly" },
  { value: "biweek", label: "Bi-weekly" },
  { value: "month", label: "Monthly" },
  { value: "quarter", label: "Quarterly" },
  { value: "year", label: "Yearly" },
];

const periodStart = (period: CalendarReportPeriod, date: Date) => {
  if (period === "week") return startOfWeek(date, { weekStartsOn: 1 });
  if (period === "biweek") return startOfWeek(addWeeks(date, -1), { weekStartsOn: 1 });
  if (period === "month") return startOfMonth(date);
  if (period === "quarter") return startOfQuarter(date);
  return startOfYear(date);
};

const periodEnd = (period: CalendarReportPeriod, date: Date) => {
  if (period === "week") return endOfWeek(date, { weekStartsOn: 1 });
  if (period === "biweek") return endOfWeek(date, { weekStartsOn: 1 });
  if (period === "month") return endOfMonth(date);
  if (period === "quarter") return endOfQuarter(date);
  return endOfYear(date);
};

export const getReportPeriodRange = (
  period: CalendarReportPeriod,
  anchorDate: Date,
  today = new Date()
): PeriodRange => {
  const end = periodEnd(period, anchorDate);
  const currentDay = startOfDay(today);
  return {
    start: periodStart(period, anchorDate),
    end: isAfter(end, currentDay) ? currentDay : end,
  };
};

export const moveReportPeriod = (
  period: CalendarReportPeriod,
  anchorDate: Date,
  direction: -1 | 1
) => {
  if (period === "week") return addWeeks(anchorDate, direction);
  if (period === "biweek") return addWeeks(anchorDate, direction * 2);
  if (period === "month") return addMonths(anchorDate, direction);
  if (period === "quarter") return addQuarters(anchorDate, direction);
  return addYears(anchorDate, direction);
};

const periodTitle = (period: CalendarReportPeriod, anchorDate: Date) => {
  if (period === "week") return `Week of ${format(periodStart(period, anchorDate), "MMM d, yyyy")}`;
  if (period === "biweek") return `Two weeks from ${format(periodStart(period, anchorDate), "MMM d, yyyy")}`;
  if (period === "month") return format(anchorDate, "MMMM yyyy");
  if (period === "quarter") return `Q${Math.floor(anchorDate.getMonth() / 3) + 1} ${format(anchorDate, "yyyy")}`;
  return format(anchorDate, "yyyy");
};

const currentPeriodLabel = (period: CalendarReportPeriod) =>
  period === "biweek" ? "Current 2 weeks" : `Current ${period}`;

interface ReportPeriodPickerProps {
  period: ReportPeriod;
  anchorDate: Date;
  startDate: Date;
  endDate: Date;
  loading?: boolean;
  appliedRange?: string;
  allowAllTime?: boolean;
  showCustomApply?: boolean;
  onPeriodChange: (period: CalendarReportPeriod) => void;
  onNavigate: (direction: -1 | 1) => void;
  onCurrentPeriod: () => void;
  onCustomMode: () => void;
  onAllTime?: () => void;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
}

export default function ReportPeriodPicker({
  period,
  anchorDate,
  startDate,
  endDate,
  loading = false,
  appliedRange,
  allowAllTime = false,
  showCustomApply = true,
  onPeriodChange,
  onNavigate,
  onCurrentPeriod,
  onCustomMode,
  onAllTime,
  onStartDateChange,
  onEndDateChange,
}: ReportPeriodPickerProps) {
  const isCalendarPeriod = PERIODS.some((item) => item.value === period);
  const calendarPeriod = isCalendarPeriod ? period as CalendarReportPeriod : null;
  const currentRange = calendarPeriod ? getReportPeriodRange(calendarPeriod, new Date()) : null;
  const shownRange = calendarPeriod ? getReportPeriodRange(calendarPeriod, anchorDate) : null;
  const isCurrent = Boolean(currentRange && shownRange && isSameDay(currentRange.start, shownRange.start));

  return (
    <section className="report-period-picker" aria-label="Report time span">
      <div className="report-period-heading">
        <div><CalendarDays size={18} /><span><strong>Time span</strong><small>Choose a calendar period or set custom dates.</small></span></div>
        {appliedRange && <em>Showing {appliedRange}</em>}
      </div>
      <div className="report-period-tabs" role="group" aria-label="Time span options">
        {PERIODS.map((item) => <button key={item.value} type="button" className={period === item.value ? "is-active" : ""} aria-pressed={period === item.value} disabled={loading} onClick={() => onPeriodChange(item.value)}>{item.label}</button>)}
        <button type="button" className={period === "custom" ? "is-active" : ""} aria-pressed={period === "custom"} disabled={loading} onClick={onCustomMode}>Custom</button>
        {allowAllTime && <button type="button" className={period === "all" ? "is-active" : ""} aria-pressed={period === "all"} disabled={loading} onClick={onAllTime}>All time</button>}
      </div>

      {calendarPeriod && shownRange && <div className="report-period-navigator">
        <button type="button" aria-label={`Previous ${calendarPeriod}`} disabled={loading} onClick={() => onNavigate(-1)}><ChevronLeft size={18} /></button>
        <div><strong>{periodTitle(calendarPeriod, anchorDate)}</strong><span>{format(shownRange.start, "MMM d, yyyy")} – {format(shownRange.end, "MMM d, yyyy")}</span></div>
        <button type="button" aria-label={`Next ${calendarPeriod}`} disabled={loading || isCurrent} onClick={() => onNavigate(1)}><ChevronRight size={18} /></button>
        {!isCurrent && <button type="button" className="report-period-current" disabled={loading} onClick={onCurrentPeriod}>{currentPeriodLabel(calendarPeriod)}</button>}
      </div>}

      {period === "custom" && <div className="report-period-custom">
        <label><span>Start date</span><DatePicker selected={startDate} onChange={(date: Date | null) => { if (date) onStartDateChange(date); }} dateFormat="MMM d, yyyy" maxDate={endDate} /></label>
        <label><span>End date</span><DatePicker selected={endDate} onChange={(date: Date | null) => { if (date) onEndDateChange(date); }} dateFormat="MMM d, yyyy" minDate={startDate} maxDate={new Date()} /></label>
        {showCustomApply && <button type="submit" className="report-period-apply" disabled={loading}><Search size={16} /> {loading ? "Loading…" : "View report"}</button>}
      </div>}

      {period === "all" && <div className="report-period-all"><strong>All recorded time</strong><span>Showing every available team member and project record.</span></div>}
    </section>
  );
}
